// ============================================================
// Datei: ManualVolunteerRegisterForm.jsx
//
// FINAL FIX:
// ✅ KEINE Defaultwerte mehr im Frontend
// ✅ Backend übernimmt komplette Logik
// 🔥 NEU: Callback an Parent → Liste wird aktualisiert
// ============================================================

import React, { useEffect, useState } from "react";
import Api from "../../Api";

import "../../pages/register/register.css";
import "./manualVolunteerRegisterForm.css";

const ManualVolunteerRegisterForm = ({ onVolunteerCreated }) => {

	// ============================================================
	// STATE
	// ============================================================

	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		phoneNumber: "",
		department: ""
	});

	const [departments, setDepartments] = useState([]);

	const [touched, setTouched] = useState({
		firstName: false,
		lastName: false,
		phoneNumber: false,
		department: false
	});

	const [message, setMessage] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	// ============================================================
	// INIT
	// ============================================================

	useEffect(() => {

		Api.get("/departments")
			.then(res => setDepartments(res.data))
			.catch(() => setDepartments([]));

		const userDept = localStorage.getItem("department");

		if (userDept) {
			setFormData(prev => ({
				...prev,
				department: userDept
			}));
		}

	}, []);

	// ============================================================
	// VALIDATION
	// ============================================================

	const isValidPhoneNumber = (phone) => {
		if (!phone) return false;
		const normalized = phone.replace(/\s+/g, "");
		return /^(\+41|0)[0-9]{9}$/.test(normalized);
	};

	const normalizePhoneNumber = (phone) => {
		const p = phone.replace(/\s+/g, "");
		return p.startsWith("0") ? "+41" + p.substring(1) : p;
	};

	const errors = {
		firstName:
			!formData.firstName.trim()
				? "Vorname erforderlich."
				: formData.firstName.trim().length < 2
					? "Mindestens 2 Zeichen."
					: null,

		lastName:
			!formData.lastName.trim()
				? "Nachname erforderlich."
				: formData.lastName.trim().length < 2
					? "Mindestens 2 Zeichen."
					: null,

		phoneNumber:
			!formData.phoneNumber
				? "Telefonnummer erforderlich."
				: !isValidPhoneNumber(formData.phoneNumber)
					? "Ungültige Nummer."
					: null,

		department:
			!formData.department
				? "Department erforderlich."
				: null
	};

	const formValid = Object.values(errors).every(e => e === null);

	// ============================================================
	// HANDLER
	// ============================================================

	const handleChange = (e) =>
		setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

	const handleBlur = (field) =>
		setTouched(prev => ({ ...prev, [field]: true }));

	// ============================================================
	// SUBMIT (🔥 CLEAN REQUEST + CALLBACK)
	// ============================================================

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!formValid || submitting) return;

		setSubmitting(true);
		setMessage(null);

		try {

			// ============================================================
			// 🔥 NUR PFLICHTFELDER SENDEN
			// ============================================================

			const res = await Api.post("/volunteers/createVolunteer", {
				firstName: formData.firstName.trim(),
				lastName: formData.lastName.trim(),
				phoneNumber: normalizePhoneNumber(formData.phoneNumber),
				department: {
					department: formData.department
				}
			});

			// ============================================================
			// 🔥 WICHTIGSTER FIX → Parent informieren
			// ============================================================
			if (onVolunteerCreated) {
				onVolunteerCreated(res.data); // → triggert reload im Parent
			}

			// SUCCESS UI
			setMessage({
				type: "success",
				text: "Helfer erfolgreich erstellt."
			});

			setFormData(prev => ({
				firstName: "",
				lastName: "",
				phoneNumber: "",
				department: prev.department
			}));

			setTouched({
				firstName: false,
				lastName: false,
				phoneNumber: false,
				department: false
			});

		} catch (err) {

			console.log("❌ FULL ERROR:", err);
			console.log("❌ RESPONSE:", err.response);

			let errorMessage = "Fehler bei Erstellung.";

			if (err.response?.data) {
				if (typeof err.response.data === "string") {
					errorMessage = err.response.data;
				} else if (typeof err.response.data === "object") {
					errorMessage =
						err.response.data.message ||
						err.response.data.error ||
						"Backend Fehler";
				}
			}

			setMessage({
				type: "error",
				text: errorMessage
			});

		} finally {
			setSubmitting(false);
		}
	};

	// ============================================================
	// RENDER
	// ============================================================

	return (
		<form onSubmit={handleSubmit} className="register-form">

			<h2 className="register-heading">Helfer hinzufügen</h2>

			{message && (
				<div className={
					message.type === "success"
						? "register-success"
						: "register-error"
				}>
					{message.text}
				</div>
			)}

			<label className="register-label">Ressort</label>
			<select
				name="department"
				value={formData.department}
				onChange={handleChange}
				onBlur={() => handleBlur("department")}
				className={`register-input ${touched.department
					? errors.department ? "input-invalid" : "input-valid"
					: ""
				}`}
			>
				<option value="">suech us</option>
				{departments.map(d => (
					<option key={d.departmentId} value={d.department}>
						{d.department}
					</option>
				))}
			</select>

			<label className="register-label">Vorname</label>
			<input
				type="text"
				name="firstName"
				value={formData.firstName}
				onChange={handleChange}
				onBlur={() => handleBlur("firstName")}
			/>

			<label className="register-label">Nachname</label>
			<input
				type="text"
				name="lastName"
				value={formData.lastName}
				onChange={handleChange}
				onBlur={() => handleBlur("lastName")}
			/>

			<label className="register-label">Telefonnummer</label>
			<input
				type="tel"
				name="phoneNumber"
				value={formData.phoneNumber}
				onChange={handleChange}
				onBlur={() => handleBlur("phoneNumber")}
			/>

			<button
				type="submit"
				className="register-button"
				disabled={!formValid || submitting}
			>
				{submitting ? "Sende..." : "einschreiben"}
			</button>

		</form>
	);
};

export default ManualVolunteerRegisterForm;
